/**
 * Business-scoped wallet APIs (v2). Paths align with backend OpenAPI:
 * /api/v2/business/{businessId}/wallet/...
 */
import { create, deleteResource, get, updatePatch } from "./apiService";
import { unwrapBusinessResponse } from "../utils/unwrapBusinessResponse";

function bpath(businessId: string, suffix: string): string {
  const id = encodeURIComponent(businessId);
  return `/api/v2/business/${id}${suffix}`;
}

export { unwrapBusinessResponse };

function num(v: unknown): number | undefined {
  if (typeof v === "number" && Number.isFinite(v)) {
    return v;
  }
  if (typeof v === "string" && v.trim() !== "") {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

/**
 * Normalizes portfolio-estimate responses after one or more `{ data: ... }` wrappers.
 */
export function extractBusinessPortfolioPayload(response: unknown): {
  usdtEstimate: number;
  zarEstimate: number;
  portfolioEstimate: string;
  portfolioEstimateRaw: number;
  assets: unknown[];
  timestamp?: string | number;
} | null {
  let cur: unknown = response;
  for (let depth = 0; depth < 4; depth++) {
    if (!cur || typeof cur !== "object") {
      return null;
    }
    if (Array.isArray(cur)) {
      return {
        usdtEstimate: 0,
        zarEstimate: 0,
        portfolioEstimate: "0.00",
        portfolioEstimateRaw: 0,
        assets: cur,
      };
    }
    const r = cur as Record<string, unknown>;
    if ("data" in r && r.data !== undefined) {
      cur = r.data;
    } else {
      break;
    }
  }
  if (Array.isArray(cur)) {
    return {
      usdtEstimate: 0,
      zarEstimate: 0,
      portfolioEstimate: "0.00",
      portfolioEstimateRaw: 0,
      assets: cur,
    };
  }
  if (!cur || typeof cur !== "object") {
    return null;
  }
  const p = cur as Record<string, unknown>;
  const assetsRaw = p.assets;
  const assets = Array.isArray(assetsRaw) ? assetsRaw : [];

  return {
    usdtEstimate: num(p.usdtEstimate) ?? 0,
    zarEstimate: num(p.zarEstimate) ?? 0,
    portfolioEstimate: String(p.portfolioEstimate ?? "0.00"),
    portfolioEstimateRaw: num(p.portfolioEstimateRaw) ?? 0,
    assets,
    timestamp: p.timestamp as string | number | undefined,
  };
}

export async function getBusinessById(businessId: string) {
  return get(bpath(businessId, ""));
}

export async function getBusinessWalletAssets(
  businessId: string,
  params?: { includeZeroBalances?: boolean | string }
) {
  const q =
    params?.includeZeroBalances !== undefined
      ? { includeZeroBalances: String(params.includeZeroBalances) }
      : { includeZeroBalances: "false" };
  return get(bpath(businessId, "/wallet/assets"), q);
}

/**
 * Unwraps GET /wallet/assets body to an assets array. Handles common API shapes
 * without calling the global trading get-assets API.
 */
export function extractWalletAssetsListFromResponse(
  response: unknown
): unknown[] {
  const asPayload = extractBusinessPortfolioPayload(response);
  if (asPayload?.assets?.length) {
    return asPayload.assets;
  }
  const unwrapped = unwrapBusinessResponse<Record<string, unknown> | unknown[]>(
    response as { data?: unknown }
  );
  if (Array.isArray(unwrapped)) {
    return unwrapped;
  }
  if (unwrapped && typeof unwrapped === "object" && "assets" in unwrapped) {
    const arr = (unwrapped as { assets?: unknown }).assets;
    return Array.isArray(arr) ? arr : [];
  }
  return [];
}

export async function getBusinessPortfolioEstimate(businessId: string) {
  return get(bpath(businessId, "/wallet/portfolio-estimate"));
}

function buildQueryString(
  params?: Record<string, string | number | boolean | undefined>
): string {
  if (!params) {
    return "";
  }
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      const str = String(value);
      if (str === "") {
        continue;
      }
      sp.set(key, str);
    }
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export async function getBusinessPortfolioPerformance(
  businessId: string,
  params?: Record<string, string | number | boolean | undefined>
) {
  const qs = buildQueryString(params);
  return get(`${bpath(businessId, "/wallet/portfolio/performance")}${qs}`);
}

export async function getBusinessWalletTransactions(
  businessId: string,
  params?: Record<string, string | number | boolean | undefined>
) {
  const qs = buildQueryString(params);
  return get(`${bpath(businessId, "/wallet/transactions")}${qs}`);
}

export async function getBusinessReceiveDetails(
  businessId: string,
  params?: { businessAssetId?: string }
) {
  return get(
    bpath(businessId, "/wallet/receive-details"),
    params
      ? Object.fromEntries(
          Object.entries(params).filter(
            ([, v]) => v !== undefined && v !== null && String(v) !== ""
          )
        )
      : undefined
  );
}

export async function postBusinessCryptoTransactionsGetQuote(
  businessId: string,
  body: unknown
) {
  return create(
    bpath(businessId, "/wallet/crypto-transactions/get-quote"),
    body
  );
}

export async function postBusinessCryptoTransactionsCreate(
  businessId: string,
  body: unknown
) {
  return create(bpath(businessId, "/wallet/crypto-transactions/create"), body);
}

export async function postBusinessBankingCreate(
  businessId: string,
  body: unknown
) {
  return create(bpath(businessId, "/banking/create"), body);
}

export async function getBusinessBanking(businessId: string) {
  return get(bpath(businessId, "/banking/get-banking"));
}

export async function getBusinessBank(
  businessId: string,
  params: { bankAccId: string }
) {
  return get(bpath(businessId, "/banking/get-bank"), params);
}

export async function patchBusinessBankingUpdate(
  businessId: string,
  body: unknown
) {
  return updatePatch(
    bpath(businessId, "/banking/update"),
    {},
    body as Record<string, unknown>
  );
}

export async function deleteBusinessBanking(
  businessId: string,
  params: { bankAccId: string }
) {
  return deleteResource(bpath(businessId, "/banking/delete"), params);
}

export async function getBusinessFiatDepositInfo(
  businessId: string,
  params?: { asset?: string }
) {
  return get(
    bpath(businessId, "/wallet/fiat/deposit-info"),
    params?.asset ? { asset: params.asset } : { asset: "zar" }
  );
}

export async function postBusinessFiatDepositsCreate(
  businessId: string,
  body: unknown
) {
  return create(bpath(businessId, "/wallet/fiat/deposits/create"), body);
}

export async function postBusinessFiatWithdrawalsGetQuote(
  businessId: string,
  body: unknown
) {
  return create(bpath(businessId, "/wallet/fiat/withdrawals/get-quote"), body);
}

export async function postBusinessFiatWithdrawalsCreate(
  businessId: string,
  body: unknown
) {
  return create(bpath(businessId, "/wallet/fiat/withdrawals/create"), body);
}

export async function postBusinessTradingGetQuote(
  businessId: string,
  body: unknown
) {
  return create(bpath(businessId, "/wallet/trading/get-quote"), body);
}

export async function postBusinessTradingCreateTrade(
  businessId: string,
  body: unknown
) {
  return create(bpath(businessId, "/wallet/trading/create-trade"), body);
}

export async function postBusinessTradingMinimumAmount(
  businessId: string,
  body: unknown
) {
  return create(bpath(businessId, "/wallet/trading/minimum-amount"), body);
}

export async function getBusinessUsers(businessId: string) {
  return get(bpath(businessId, "/users"));
}

export async function postBusinessUsers(businessId: string, body: unknown) {
  return create(bpath(businessId, "/users"), body);
}

export async function patchBusinessUser(
  businessId: string,
  targetUserId: string,
  body: unknown
) {
  return updatePatch(
    bpath(businessId, `/users/${encodeURIComponent(targetUserId)}`),
    {},
    body as Record<string, unknown>
  );
}

export async function postBusinessUserSuspend(
  businessId: string,
  targetUserId: string,
  body?: unknown
) {
  return create(
    bpath(businessId, `/users/${encodeURIComponent(targetUserId)}/suspend`),
    body ?? {}
  );
}
